import type {
  Audience,
  ColumnRule,
  RankDirection,
  RankWindow,
  RepMaxExercise,
} from "../../../types/leaderboard";
import { getFullName } from "../../../utils/personal-records";

// Per-column ranking rules for member leaderboards. Must stay in sync with
// msb-api lib/api_helpers/leaderboard-ranking.js (getBoardColumns).

export const AUDIENCES: Audience[] = ["pull", "push", "gym"];
export const MIN_RANKED_MEMBERS = 8;

// "follow" = the coach's date range here, last 12 months on member boards.
export const WINDOWS: RankWindow[] = ["follow", "14", "30", "60", "90", "365"];

// Columns members never see.
export const COACH_ONLY_COLUMNS = new Set(["photos", "programTeam"]);

const COUNT_COLUMNS = new Set([
  "workouts",
  "totalVolume",
  "totalReps",
  "totalSets",
]);

/** Rule key of a rep-max exercise, same as the API's. */
export const repMaxRuleKey = (exercise: RepMaxExercise): string =>
  `rm:${getFullName(exercise)}:${exercise.repTarget}`;

export const measurementRuleKey = (key: string): string => `bm:${key}`;

/** Lower ranks first for body changes; higher everywhere else. */
export const defaultDirection = (ruleKey: string): RankDirection =>
  ruleKey === "bodyweight" || ruleKey.startsWith("bm:") ? "asc" : "desc";

export const getRule = (
  columnRules: ColumnRule[] = [],
  ruleKey: string
): Required<ColumnRule> => {
  const rule: Partial<ColumnRule> =
    columnRules.find((r) => r.key === ruleKey) || {};
  return {
    key: ruleKey,
    window: rule.window || "follow",
    direction: rule.direction || defaultDirection(ruleKey),
  };
};

/** Rule keys of the columns a template ranks members on, in table order. */
export const rankedRuleKeys = ({
  columns = [],
  bodyMeasurements = [],
  repMaxExercises = [],
}: {
  columns?: Iterable<string>;
  bodyMeasurements?: Iterable<string>;
  repMaxExercises?: RepMaxExercise[];
}): string[] => [
  ...[...columns].filter((key) => !COACH_ONLY_COLUMNS.has(key)),
  ...[...bodyMeasurements].map(measurementRuleKey),
  ...repMaxExercises.map(repMaxRuleKey),
];

/**
 * Helper warning for a rule, or null:
 * - tenure: a count over the longest window rewards long-standing members
 * - shortLift: a lift over 14/30 days drops anyone who didn't test recently
 */
export const ruleWarning = ({
  key,
  window,
}: Pick<ColumnRule, "key" | "window">): "tenure" | "shortLift" | null => {
  if (COUNT_COLUMNS.has(key) && (window === "follow" || window === "365")) {
    return "tenure";
  }
  if (key.startsWith("rm:") && (window === "14" || window === "30")) {
    return "shortLift";
  }
  return null;
};

/** Short header badge for a pinned window ("30D", "12M"); null when following. */
export const windowBadge = (window?: RankWindow | null): string | null => {
  if (!window || window === "follow") return null;
  return window === "365" ? "12M" : `${window}D`;
};
